import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from '@/api/client';
import {
  deleteInformationDisclosureAgreement,
  getInformationDisclosureAgreementViewUrl,
  uploadInformationDisclosureAgreement,
} from '@/api/clientAgreements';
import { useAuthStore } from '@/state/authStore';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingState } from '@/components/Spinner';
import type { SessionStatus } from '@/types/session';
import { formatSessionDay, isNoSectionMeetingType } from '@/utils/meetingType';

function SourceBadge({ source }: { source: string }) {
  const config: Record<string, { label: string; cls: string }> = {
    default: { label: 'デフォルト', cls: 'bg-blue-100 text-blue-700' },
    manual: { label: '手動追加', cls: 'bg-green-100 text-green-700' },
    admin: { label: '管理者設定', cls: 'bg-orange-100 text-orange-700' },
  };
  const c = config[source] ?? { label: source, cls: 'bg-gray-100 text-gray-600' };
  return <span className={`rounded px-2 py-0.5 text-xs font-medium ${c.cls}`}>{c.label}</span>;
}

interface ClientSession {
  id: string; coach_user_id: string;
  day_type: string; serial_no: number; status: SessionStatus;
  day1_variant?: 'first' | 'repeat' | null;
  outcome: string | null; session_date: string | null; updated_at: string;
  status_detail: 'uploading' | 'upload_failed' | 'start_pending' | null;
  requires_my_action: boolean;
  purged?: boolean;
}

interface ClientAgreement {
  id: string;
  filename: string;
  content_type: string;
  uploaded_at: string;
}

interface ClientDetail {
  id: string; name: string; owner_coach_id: string; coach_name: string;
  company_overview_url?: string | null;
  information_disclosure_agreement_uploaded?: boolean;
  information_disclosure_agreement_filename?: string | null;
  information_disclosure_agreement_uploaded_at?: string | null;
  information_disclosure_agreements?: ClientAgreement[];
  sessions: ClientSession[];
  can_manage_feedbackers: boolean;
  assigned_feedbacker_names: string[];
  feedbackers: Array<{ user_id: string; user_name: string; email: string; assignment_source: string }>;
}

const COMPANY_OVERVIEW_URL_MAX_LENGTH = 2048;
interface Feedbacker { id: string; name: string; email: string }

function statusLabel(s: ClientSession) {
  if (s.purged) return '削除済み';
  if (s.status_detail === 'uploading') return '音声アップロード中';
  if (s.status_detail === 'upload_failed') return '音声アップロード失敗';
  if (s.status_detail === 'start_pending') return '処理開始待ち';
  return undefined;
}

function statusClass(s: ClientSession) {
  if (s.purged) return 'bg-gray-200 text-gray-600';
  if (s.status_detail === 'uploading') return 'bg-blue-100 text-blue-800';
  if (s.status_detail === 'upload_failed') return 'bg-red-100 text-red-800';
  if (s.status_detail === 'start_pending') return 'bg-yellow-100 text-yellow-800';
  return undefined;
}

function formatUploadedAt(value: string | null | undefined) {
  if (!value) return 'アップロード日時不明';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore(s => s.currentUser);
  const locationState = location.state as { clientListSearch?: unknown } | null;
  const clientListSearch = typeof locationState?.clientListSearch === 'string'
    ? new URLSearchParams(locationState.clientListSearch).toString()
    : '';
  const clientListPath = clientListSearch ? `/clients?${clientListSearch}` : '/clients';
  const [data, setData] = useState<ClientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [clientNameInput, setClientNameInput] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [clientUrlInput, setClientUrlInput] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editingSessionDateId, setEditingSessionDateId] = useState<string | null>(null);
  const [sessionDateInput, setSessionDateInput] = useState('');
  const [savingSessionDate, setSavingSessionDate] = useState(false);
  const [editingFeedbackers, setEditingFeedbackers] = useState(false);
  const [availableFeedbackers, setAvailableFeedbackers] = useState<Feedbacker[]>([]);
  const [selectedFeedbackerIds, setSelectedFeedbackerIds] = useState<Set<string>>(() => new Set());
  const [loadingFeedbackers, setLoadingFeedbackers] = useState(false);
  const [savingFeedbackers, setSavingFeedbackers] = useState(false);
  const [agreementFile, setAgreementFile] = useState<File | null>(null);
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [openingAgreement, setOpeningAgreement] = useState(false);
  const [deletingAgreementId, setDeletingAgreementId] = useState<string | null>(null);
  const [agreementPreview, setAgreementPreview] = useState<{ url: string; filename: string } | null>(null);
  const localAgreementPreviewUrlRef = useRef<string | null>(null);

  const handleSessionClick = (s: ClientSession) => {
    if (s.status === 'feedback_pending' || s.status === 'feedback_in_progress') {
      navigate(
        user?.is_feedbacker && s.requires_my_action
          ? `/sessions/${s.id}/review`
          : `/sessions/${s.id}/feedback`,
      );
    } else if (
      s.status === 'overall_feedback_pending' ||
      s.status === 'overall_analysis_approval_pending' ||
      s.status === 'coach_ack_pending' ||
      s.status === 'completed'
    ) {
      navigate(`/sessions/${s.id}/feedback`);
    } else if (s.status === 'section_config_pending') {
      if (isNoSectionMeetingType(s.day_type)) {
        navigate(`/sessions/${s.id}/status?jobId=${s.id}`);
        return;
      }
      navigate(`/sessions/${s.id}/sections?jobId=${s.id}&day=${encodeURIComponent(s.day_type)}`);
    } else {
      navigate(`/sessions/${s.id}/status?jobId=${s.id}`);
    }
  };
  const canOpenSession = (s: ClientSession) => {
    const isCoachOwner = String(s.coach_user_id) === String(user?.id);
    const isVisibleFinalStatus =
      s.status === 'overall_feedback_pending' ||
      s.status === 'overall_analysis_approval_pending' ||
      s.status === 'coach_ack_pending' ||
      s.status === 'completed';
    const isFailedStatus = s.status === 'transcribing_failed' || s.status === 'ai_evaluation_failed';
    const isProcessingStatus =
      s.status === 'transcribing' ||
      s.status === 'section_config_pending' ||
      s.status === 'ai_evaluating';
    return Boolean(
        user?.is_admin ||
        s.requires_my_action ||
        (isProcessingStatus && isCoachOwner) ||
        (isFailedStatus && isCoachOwner) ||
        (isVisibleFinalStatus && (isCoachOwner || user?.is_feedbacker)),
    );
  };

  const fetchClient = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    api.get(`/clients/${clientId}`).then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [clientId]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const clearLocalAgreementPreviewUrl = useCallback(() => {
    if (localAgreementPreviewUrlRef.current) {
      URL.revokeObjectURL(localAgreementPreviewUrlRef.current);
      localAgreementPreviewUrlRef.current = null;
    }
  }, []);

  useEffect(() => clearLocalAgreementPreviewUrl, [clearLocalAgreementPreviewUrl]);

  const canManageClient = Boolean(user?.is_admin || String(data?.owner_coach_id) === String(user?.id));
  const canEditDefaultFeedbackers = Boolean(user?.is_admin);

  const startEditName = () => {
    setClientNameInput(data?.name ?? '');
    setEditingName(true);
  };

  const handleSaveName = async () => {
    const name = clientNameInput.trim();
    if (!clientId || !name) return;
    setSavingName(true);
    try {
      await api.patch(`/clients/${clientId}`, { name });
      setEditingName(false);
      await fetchClient();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string | { message?: string } } } }).response?.data?.detail;
      alert(typeof msg === 'string' ? msg : msg?.message ?? 'クライアント名の変更に失敗しました');
    } finally {
      setSavingName(false);
    }
  };

  const startEditUrl = () => {
    setClientUrlInput(data?.company_overview_url ?? '');
    setUrlError(null);
    setEditingUrl(true);
  };

  const handleSaveUrl = async () => {
    if (!clientId) return;
    const value = clientUrlInput.trim();
    if (value.length > COMPANY_OVERVIEW_URL_MAX_LENGTH) {
      setUrlError(`URLは${COMPANY_OVERVIEW_URL_MAX_LENGTH}文字以内で入力してください`);
      return;
    }
    if (value && !/^https?:\/\//i.test(value)) {
      setUrlError('http:// または https:// で始まるURLを入力してください');
      return;
    }
    setUrlError(null);
    setSavingUrl(true);
    try {
      await api.patch(`/clients/${clientId}`, { company_overview_url: value });
      setEditingUrl(false);
      await fetchClient();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string | { message?: string } } } }).response?.data?.detail;
      alert(typeof msg === 'string' ? msg : msg?.message ?? '会社概要URLの変更に失敗しました');
    } finally {
      setSavingUrl(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientId || !data) return;
    const sessionCount = data.sessions.length;
    const message = sessionCount > 0
      ? `このクライアントには ${sessionCount} 件のセッションがあります。\n削除すると、関連するセッションも一覧から表示されなくなります。\n\n削除しますか？`
      : 'このクライアントを削除しますか？';
    if (!confirm(message)) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${clientId}`);
      navigate(clientListPath);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string | { message?: string } } } }).response?.data?.detail;
      alert(typeof msg === 'string' ? msg : msg?.message ?? 'クライアントの削除に失敗しました');
      setDeleting(false);
    }
  };

  const startEditSessionDate = (session: ClientSession) => {
    setEditingSessionDateId(session.id);
    setSessionDateInput(session.session_date ?? new Date().toISOString().split('T')[0] ?? '');
  };

  const cancelEditSessionDate = () => {
    setEditingSessionDateId(null);
    setSessionDateInput('');
  };

  const handleSaveSessionDate = async (sessionId: string) => {
    if (!sessionDateInput) return;
    setSavingSessionDate(true);
    try {
      await api.patch(`/sessions/${sessionId}`, { session_date: sessionDateInput });
      cancelEditSessionDate();
      await fetchClient();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string | { message?: string } } } }).response?.data?.detail;
      alert(typeof msg === 'string' ? msg : msg?.message ?? '実施日の変更に失敗しました');
    } finally {
      setSavingSessionDate(false);
    }
  };

  const startEditFeedbackers = async () => {
    if (!data?.can_manage_feedbackers) return;
    setEditingFeedbackers(true);
    setSelectedFeedbackerIds(new Set(
      data.feedbackers
        .filter(fb => canEditDefaultFeedbackers || fb.assignment_source !== 'default')
        .map(fb => fb.user_id),
    ));
    setLoadingFeedbackers(true);
    try {
      const resp = await api.get('/admin/feedbackers');
      setAvailableFeedbackers(resp.data);
    } catch {
      setAvailableFeedbackers([]);
    } finally {
      setLoadingFeedbackers(false);
    }
  };

  const cancelEditFeedbackers = () => {
    setEditingFeedbackers(false);
    setSelectedFeedbackerIds(new Set());
  };

  const toggleFeedbacker = (id: string) => {
    setSelectedFeedbackerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSaveFeedbackers = async () => {
    if (!clientId) return;
    setSavingFeedbackers(true);
    try {
      await api.patch(`/clients/${clientId}`, { feedbacker_ids: Array.from(selectedFeedbackerIds) });
      setEditingFeedbackers(false);
      await fetchClient();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data;
      alert(
        typeof msg?.detail === 'string'
          ? msg.detail
          : msg?.detail?.message ?? msg?.message ?? 'フィードバッカーの変更に失敗しました',
      );
    } finally {
      setSavingFeedbackers(false);
    }
  };

  const handleUploadAgreement = async () => {
    if (!clientId || !agreementFile) return;
    setUploadingAgreement(true);
    try {
      await uploadInformationDisclosureAgreement(clientId, agreementFile);
      setAgreementFile(null);
      setAgreementPreview(null);
      clearLocalAgreementPreviewUrl();
      await fetchClient();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data?.detail;
      alert(
        typeof detail === 'string'
          ? detail
          : detail?.message ?? (e instanceof Error ? e.message : '合意書PDFのアップロードに失敗しました'),
      );
    } finally {
      setUploadingAgreement(false);
    }
  };

  const handleAgreementFileChange = (file: File | null) => {
    setAgreementPreview(null);
    clearLocalAgreementPreviewUrl();
    setAgreementFile(file);
  };

  const handlePreviewSelectedAgreementFile = () => {
    if (!agreementFile) return;
    clearLocalAgreementPreviewUrl();
    const url = URL.createObjectURL(agreementFile);
    localAgreementPreviewUrlRef.current = url;
    setAgreementPreview({ url, filename: agreementFile.name });
  };

  const handleOpenAgreement = async (agreement: ClientAgreement) => {
    if (!clientId) return;
    setAgreementPreview(null);
    clearLocalAgreementPreviewUrl();
    setOpeningAgreement(true);
    try {
      const { url, filename } = await getInformationDisclosureAgreementViewUrl(clientId, agreement.id);
      setAgreementPreview({ url, filename });
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data?.detail;
      alert(
        typeof detail === 'string'
          ? detail
          : detail?.message ?? (e instanceof Error ? e.message : '合意書PDFを表示できませんでした'),
      );
    } finally {
      setOpeningAgreement(false);
    }
  };

  const closeAgreementPreview = () => {
    setAgreementPreview(null);
    clearLocalAgreementPreviewUrl();
  };

  const handleDeleteAgreement = async (agreement: ClientAgreement) => {
    if (!clientId) return;
    if (!confirm(`${agreement.filename} を削除します。よろしいですか？`)) return;
    setDeletingAgreementId(agreement.id);
    try {
      await deleteInformationDisclosureAgreement(clientId, agreement.id);
      await fetchClient();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string | { message?: string }; message?: string } } }).response?.data?.detail;
      alert(
        typeof detail === 'string'
          ? detail
          : detail?.message ?? (e instanceof Error ? e.message : '合意書PDFの削除に失敗しました'),
      );
    } finally {
      setDeletingAgreementId(null);
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><LoadingState /></div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
  if (!data) return null;

  const defaultFeedbackers = data.feedbackers.filter(fb => fb.assignment_source === 'default');
  const defaultFeedbackerIds = new Set(defaultFeedbackers.map(fb => fb.user_id));
  const assignedFeedbackerNames = data.assigned_feedbacker_names ?? data.feedbackers.map(fb => fb.user_name);
  const agreements = data.information_disclosure_agreements ?? [];
  const hasAgreement = agreements.length > 0 || data.information_disclosure_agreement_uploaded === true;
  const showAssignmentType = user?.role === 'feedbacker' && !user.is_admin;
  const isOwnClient = String(data.owner_coach_id) === String(user?.id);

  return (
    <div>
      <div className="mb-4"><Link to={clientListPath} className="text-sm text-blue-600 hover:underline">← クライアント一覧</Link></div>
      <div className="flex items-center justify-between">
        <div>
          {showAssignmentType && (
            <div className="mb-2">
              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                isOwnClient
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-amber-100 text-amber-800'
              }`}>
                {isOwnClient
                  ? '自身が担当するクライアント'
                  : 'フィードバック対象のクライアント'}
              </span>
            </div>
          )}
          {editingName ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={clientNameInput}
                onChange={e => setClientNameInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSaveName()}
                className="rounded border border-gray-300 px-3 py-1.5 text-xl font-semibold text-gray-900"
                autoFocus
              />
              <button
                type="button"
                onClick={handleSaveName}
                disabled={savingName || !clientNameInput.trim()}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                保存
              </button>
              <button
                type="button"
                onClick={() => setEditingName(false)}
                disabled={savingName}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600"
              >
                取消
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-gray-900">{data.name}</h1>
              {canManageClient && (
                <button type="button" onClick={startEditName} className="text-sm text-blue-600 hover:underline">
                  名称変更
                </button>
              )}
            </div>
          )}
          <p className="mt-1 text-sm text-gray-500">担当コーチ: {data.coach_name}</p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span>会社概要URL:</span>
            {editingUrl ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="url"
                  value={clientUrlInput}
                  onChange={e => {
                    setClientUrlInput(e.target.value);
                    if (urlError) setUrlError(null);
                  }}
                  onKeyDown={e => e.key === 'Enter' && handleSaveUrl()}
                  placeholder="https://example.com/about"
                  maxLength={COMPANY_OVERVIEW_URL_MAX_LENGTH}
                  className="w-80 rounded border border-gray-300 px-2 py-1 text-sm text-gray-900"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSaveUrl}
                  disabled={savingUrl}
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  保存
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingUrl(false);
                    setUrlError(null);
                  }}
                  disabled={savingUrl}
                  className="rounded border border-gray-300 px-3 py-1 text-sm text-gray-600"
                >
                  取消
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {data.company_overview_url ? (
                  <a
                    href={data.company_overview_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="break-all text-blue-600 hover:underline"
                  >
                    {data.company_overview_url}
                  </a>
                ) : (
                  <span className="text-gray-400">未設定</span>
                )}
                {canManageClient && (
                  <button
                    type="button"
                    onClick={startEditUrl}
                    className="rounded border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {data.company_overview_url ? 'URLを変更' : 'URLを設定'}
                  </button>
                )}
              </div>
            )}
          </div>
          {urlError && (
            <p className="mt-1 text-xs text-red-600">{urlError}</p>
          )}
        </div>
        {canManageClient && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/clients/${clientId}/upload`)}
              disabled={!hasAgreement}
              title={!hasAgreement ? '合意書PDFをアップロードするとセッションを追加できます' : undefined}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              + 新規セッション追加
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <h2 className="text-sm font-semibold text-gray-700">セッション一覧（{data.sessions.length}件）</h2>
          <div className="mt-2 overflow-hidden rounded-lg bg-white shadow-sm">
            {data.sessions.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">セッションはまだありません</div>
            ) : (
              <table className="w-full">
                <thead className="border-b bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 w-20">要対応</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">実施日</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Day</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">ステータス</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.sessions.map(s => {
                    const canOpen = canOpenSession(s);
                    return (
                    <tr
                      key={s.id}
                      onClick={canOpen ? () => handleSessionClick(s) : undefined}
                      className={
                        s.requires_my_action
                          ? 'bg-red-50 font-semibold hover:bg-red-100 cursor-pointer'
                          : canOpen
                            ? 'cursor-pointer text-gray-700 hover:bg-gray-50'
                            : 'cursor-default text-gray-400'
                      }
                    >
                      <td className="px-3 py-3">
                        {s.requires_my_action && (
                          <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                            要対応
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {editingSessionDateId === s.id ? (
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="date"
                              value={sessionDateInput}
                              onChange={e => setSessionDateInput(e.target.value)}
                              className="rounded border border-gray-300 px-2 py-1 text-sm"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveSessionDate(s.id)}
                              disabled={savingSessionDate || !sessionDateInput}
                              className="rounded bg-blue-600 px-2.5 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              保存
                            </button>
                            <button
                              type="button"
                              onClick={cancelEditSessionDate}
                              disabled={savingSessionDate}
                              className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-600"
                            >
                              取消
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span>{s.session_date ?? '—'}</span>
                            {canManageClient && (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  startEditSessionDate(s);
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                変更
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatSessionDay(s.day_type, s.day1_variant)}</td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={s.status}
                          labelOverride={statusLabel(s)}
                          classNameOverride={statusClass(s)}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="col-span-1">
          <div className={`mb-6 rounded-lg border p-3 ${
            hasAgreement ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
          }`}>
            <h2 className={`text-sm font-semibold ${hasAgreement ? 'text-green-900' : 'text-red-900'}`}>
              情報開示の合意書PDF
            </h2>
            <p className={`mt-1 text-xs ${hasAgreement ? 'text-green-700' : 'text-red-700'}`}>
              {hasAgreement
                ? `アップロード済み: ${agreements.length}件`
                : '未アップロードのため、セッションを追加できません'}
            </p>
            {canManageClient && (
              <div className="mt-3 space-y-3">
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={e => handleAgreementFileChange(e.target.files?.[0] ?? null)}
                  className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-xs"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={handlePreviewSelectedAgreementFile}
                    disabled={!agreementFile || uploadingAgreement}
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    プレビュー
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadAgreement}
                    disabled={!agreementFile || uploadingAgreement}
                    className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {uploadingAgreement ? 'アップロード中...' : 'PDFを追加'}
                  </button>
                </div>
              </div>
            )}
            {agreements.length > 0 && (
              <div className="mt-3 space-y-2">
                {agreements.map((agreement) => (
                  <div key={agreement.id} className="rounded border border-green-200 bg-white p-2">
                    <p className="break-all text-xs font-medium text-gray-900">{agreement.filename}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {formatUploadedAt(agreement.uploaded_at)}
                    </p>
                    {canManageClient && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenAgreement(agreement)}
                          disabled={openingAgreement}
                          className="rounded border border-green-300 bg-white px-2.5 py-1 text-xs font-medium text-green-800 hover:bg-green-100 disabled:opacity-50"
                        >
                          {openingAgreement ? '表示準備中...' : '表示'}
                        </button>
                        {user?.is_admin && (
                          <button
                            type="button"
                            onClick={() => handleDeleteAgreement(agreement)}
                            disabled={deletingAgreementId === agreement.id}
                            className="rounded border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deletingAgreementId === agreement.id ? '削除中' : '削除'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {agreementPreview && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="agreement-pdf-title"
            >
              <div className="flex h-[86vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
                  <h2 id="agreement-pdf-title" className="truncate text-sm font-semibold text-gray-900">
                    {agreementPreview.filename}
                  </h2>
                  <button
                    type="button"
                    onClick={closeAgreementPreview}
                    className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    閉じる
                  </button>
                </div>
                <iframe
                  title="情報開示の合意書PDF"
                  src={agreementPreview.url}
                  className="min-h-0 flex-1 border-0"
                />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-700">フィードバッカー（{assignedFeedbackerNames.length}名）</h2>
            {data.can_manage_feedbackers && !editingFeedbackers && (
              <button type="button" onClick={startEditFeedbackers} className="text-xs text-blue-600 hover:underline">
                編集
              </button>
            )}
          </div>
          <div className="mt-2 space-y-2">
            {editingFeedbackers ? (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                {loadingFeedbackers ? (
                  <p className="text-xs text-gray-500">読み込み中...</p>
                ) : (() => {
                  const merged = new Map<string, Feedbacker>();
                  availableFeedbackers.forEach(fb => {
                    if (canEditDefaultFeedbackers || !defaultFeedbackerIds.has(fb.id)) merged.set(fb.id, fb);
                  });
                  data.feedbackers.filter(
                    fb => canEditDefaultFeedbackers || fb.assignment_source !== 'default',
                  ).forEach(fb => {
                    if (!merged.has(fb.user_id)) {
                      merged.set(fb.user_id, {
                        id: fb.user_id,
                        name: fb.user_name,
                        email: fb.email,
                      });
                    }
                  });
                  const feedbackers = Array.from(merged.values());
                  return feedbackers.length === 0 ? (
                    <div className="space-y-2">
                      {!canEditDefaultFeedbackers && defaultFeedbackers.length > 0 && (
                        <div className="space-y-1 rounded border border-gray-200 bg-white p-2">
                          {defaultFeedbackers.map(fb => (
                            <div key={fb.user_id} className="flex items-center justify-between gap-2 px-2 py-1">
                              <div className="min-w-0">
                                <p className="truncate text-sm text-gray-700">{fb.user_name}</p>
                                <p className="truncate text-xs text-gray-400">{fb.email}</p>
                              </div>
                              <SourceBadge source={fb.assignment_source} />
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-gray-400">追加できるフィードバッカーがいません</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {!canEditDefaultFeedbackers && defaultFeedbackers.length > 0 && (
                        <div className="space-y-1 rounded border border-gray-200 bg-white p-2">
                          {defaultFeedbackers.map(fb => (
                            <div key={fb.user_id} className="flex items-center justify-between gap-2 px-2 py-1">
                              <div className="min-w-0">
                                <p className="truncate text-sm text-gray-700">{fb.user_name}</p>
                                <p className="truncate text-xs text-gray-400">{fb.email}</p>
                              </div>
                              <SourceBadge source={fb.assignment_source} />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="max-h-56 space-y-1 overflow-y-auto rounded border border-gray-200 bg-white p-2">
                        {feedbackers.map(fb => (
                          <label key={fb.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50">
                            <input
                              type="checkbox"
                              checked={selectedFeedbackerIds.has(fb.id)}
                              onChange={() => toggleFeedbacker(fb.id)}
                            />
                            <span className="text-sm">{fb.name}</span>
                            <span className="min-w-0 truncate text-xs text-gray-400">{fb.email}</span>
                            {defaultFeedbackerIds.has(fb.id) && <SourceBadge source="default" />}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div className="mt-3 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={cancelEditFeedbackers}
                    disabled={savingFeedbackers}
                    className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600"
                  >
                    キャンセル
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveFeedbackers}
                    disabled={savingFeedbackers || loadingFeedbackers}
                    className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingFeedbackers ? '保存中...' : '保存'}
                  </button>
                </div>
              </div>
            ) : data.feedbackers.length > 0 ? data.feedbackers.map(fb => (
                <div key={fb.user_id} className="rounded-lg bg-white p-3 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{fb.user_name}</p>
                      <p className="text-xs text-gray-400">{fb.email}</p>
                    </div>
                    <SourceBadge source={fb.assignment_source} />
                  </div>
                </div>
              )) : assignedFeedbackerNames.map(name => (
                <div key={name} className="rounded-lg bg-white p-3 shadow-sm">
                  <p className="text-sm font-medium text-gray-900">{name}</p>
                </div>
              ))}
          </div>
          {data.can_manage_feedbackers && (
            <p className="mt-3 text-xs text-gray-400">
              {canEditDefaultFeedbackers
                ? '選択内容は初期割当を含む完全な担当者一覧として、フィードバック完了前のセッションに反映されます。'
                : 'デフォルトはここでは変更できません。追加分はフィードバック完了前のセッションに反映されます。'}
            </p>
          )}
        </div>
      </div>

      {user?.is_admin && (
        <div className="mt-8 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="mt-1 text-sm text-red-600">
            クライアントを削除すると、このクライアントに紐づくセッションは一覧から表示されなくなります。
          </p>
          <button
            type="button"
            onClick={handleDeleteClient}
            disabled={deleting}
            className="mt-3 rounded border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-50"
          >
            {deleting ? '削除中...' : 'クライアントを削除'}
          </button>
        </div>
      )}
    </div>
  );
}
